import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgStyle } from '@angular/common';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { Company } from '../../contracts/company.interface';
import { Contact, ContactTeam } from '../../contracts/contact.interface';
import { CONVERSATION_AVATAR_COLORS } from '../../../shared/constants/conversation-avatar-map';

@Component({
  selector: 'kc-companies-table',
  standalone: true,
  imports: [NgStyle, TableModule, Button, Tooltip],
  templateUrl: './companies-table.component.html',
})
export class CompaniesTableComponent {
  @Input() companies: Company[] = [];

  @Output() createCompany = new EventEmitter<void>();
  @Output() editCompany = new EventEmitter<Company>();
  @Output() deleteCompany = new EventEmitter<Company>();

  expandedRows: { [key: string]: boolean } = {};

  onRowExpand(event: any): void {
    this.expandedRows[event.data.id] = true;
  }

  onRowCollapse(event: any): void {
    delete this.expandedRows[event.data.id];
  }

  getCompanyInitials(company: Company): string {
    const words = company.name.trim().split(/\s+/);
    return words.length >= 2
      ? (words[0][0] + words[1][0]).toUpperCase()
      : company.name.substring(0, 2).toUpperCase();
  }

  getCompanyAvatarStyle(company: Company): { background: string; color: string } {
    return CONVERSATION_AVATAR_COLORS[((company.id ?? 0) % 10) + 1];
  }

  getContactInitials(contact: Contact): string {
    const f = contact.firstName?.charAt(0) ?? '';
    const l = contact.lastName?.charAt(0) ?? '';
    return (f + l).toUpperCase() || '?';
  }

  getContactAvatarStyle(contact: Contact): { background: string; color: string } {
    return CONVERSATION_AVATAR_COLORS[((contact.id ?? 0) % 10) + 1] ?? CONVERSATION_AVATAR_COLORS[1];
  }

  primaryPhone(contact: Contact): string {
    const phones = contact.phoneNumbers ?? [];
    return [...phones]
      .sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0))[0]
      ?.phoneNumber ?? '—';
  }

  getUserInitials(user: { firstName?: string; lastName?: string; email?: string }): string {
    const f = user.firstName?.charAt(0) ?? '';
    const l = user.lastName?.charAt(0) ?? '';
    return (f + l).toUpperCase() || user.email?.charAt(0).toUpperCase() || '?';
  }

  getUserAvatarStyle(user: { id?: number }): { background: string; color: string } {
    return CONVERSATION_AVATAR_COLORS[((user.id ?? 0) % 10) + 1] ?? CONVERSATION_AVATAR_COLORS[1];
  }

  getUserDisplayName(user: { firstName?: string; lastName?: string; email?: string }): string {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email || '';
  }

  getTeamMembersTooltip(team: ContactTeam): string {
    if (!team.members?.length) return team.name;
    return team.members.map(m => this.getUserDisplayName(m)).join(', ');
  }

  getExtraTeamsTooltip(contact: Contact): string {
    return (contact.teams ?? []).slice(1).map(t => t.name).join(', ');
  }
}
